import React, { useEffect, useState, useMemo } from "react";
import { Card, Col, Row, Select, Tag, Statistic, Progress } from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  PauseCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { GetAssignedTasksApi } from "../../api/tasks/GetAssignedTasksApi";
import GetPositionsPerformanceApi from "../../api/individuals/GetPositionsPerformanceApi";
import PageLoader from "../sub/PageLoader";
import moment from "moment";

const { Option } = Select;

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export default function IndividualTaskReport() {
  const [positions, setPositions] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const positionState = useSelector((state) => state.positionState.value);

  useEffect(() => {
    if (perfCycleState?.id && positionState?.id) {
      fetchPositions();
      // Default to logged-in user's position
      setSelectedPosition(positionState.id);
    }
  }, [perfCycleState, positionState]);

  useEffect(() => {
    if (selectedPosition && perfCycleState?.id) {
      fetchTasks();
    }
  }, [selectedPosition, perfCycleState]);

  async function fetchPositions() {
    setPositionsLoading(true);
    const response = await GetPositionsPerformanceApi(
      perfCycleState.id,
      1,
      positionState.id,
      100
    );
    if (response.message === "success") {
      setPositions(response.data?.positions || []);
    }
    setPositionsLoading(false);
  }

  async function fetchTasks() {
    setLoading(true);
    const response = await GetAssignedTasksApi(selectedPosition, perfCycleState.id);
    if (response.success) {
      setTasks(response.data || []);
    }
    setLoading(false);
  }

  const stats = useMemo(() => {
    const total = tasks.length;
    const completed = tasks.filter((t) => t.completionStatus === true).length;
    const pending = tasks.filter((t) => !t.completionStatus && !t.onHold).length;
    const onHold = tasks.filter((t) => t.onHold === true).length;
    const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;

    return { total, completed, pending, onHold, completionRate };
  }, [tasks]);

  const monthlyData = useMemo(() => {
    const result = [];
    for (let m = 0; m < 12; m++) {
      const tasksInMonth = tasks.filter((task) => {
        if (!task.taskEndDate) return false;
        return new Date(task.taskEndDate).getMonth() === m;
      });
      const completed = tasksInMonth.filter((t) => t.completionStatus === true).length;
      result.push({
        month: MONTHS[m],
        total: tasksInMonth.length,
        completed,
        percentage: tasksInMonth.length > 0 ? Math.round((completed / tasksInMonth.length) * 100) : null,
      });
    }
    return result;
  }, [tasks]);

  const selectedPositionData = positions.find((p) => p.id === selectedPosition);

  return (
    <div>
      {/* Section Header */}
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1a202c", margin: 0 }}>Individual Report</h3>
        <div style={{ fontSize: 12, color: "#94a3b8" }}>Task breakdown and weekly history per position</div>
      </div>

      {/* Position Selector */}
      <div className="mb-6">
        <span className="text-sm text-gray-500 mr-3">Select Position:</span>
        <Select
          showSearch
          style={{ width: 300 }}
          placeholder="Select a position"
          value={selectedPosition}
          onChange={(value) => setSelectedPosition(value)}
          loading={positionsLoading}
          optionFilterProp="children"
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {positions.map((pos) => (
            <Option key={pos.id} value={pos.id}>
              {pos.users?.firstName || "-"} {pos.users?.lastName || ""} ({pos.designations?.abbreviation || pos.designations?.name || "-"})
            </Option>
          ))}
        </Select>
      </div>

      {loading ? (
        <PageLoader />
      ) : tasks.length === 0 ? (
        <div className="flex justify-center items-center" style={{ minHeight: 300 }}>
          <div className="text-center">
            <div className="text-3xl text-gray-400">
              <WarningOutlined />
            </div>
            <div className="text-lg pri-font text-gray-400">
              No tasks found for this position
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <Row gutter={16} className="mb-6">
            <Col span={4}>
              <Card size="small">
                <Statistic title="Total Tasks" value={stats.total} />
              </Card>
            </Col>
            <Col span={4}>
              <Card size="small">
                <Statistic
                  title="Completed"
                  value={stats.completed}
                  valueStyle={{ color: "#52c41a" }}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card size="small">
                <Statistic
                  title="Pending"
                  value={stats.pending}
                  valueStyle={{ color: "#fa8c16" }}
                  prefix={<ClockCircleOutlined />}
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card size="small">
                <Statistic
                  title="On Hold"
                  value={stats.onHold}
                  valueStyle={{ color: "#faad14" }}
                  prefix={<PauseCircleOutlined />}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small">
                <div className="text-xs text-gray-500 mb-1">Completion Rate</div>
                <Progress
                  percent={stats.completionRate}
                  strokeColor={stats.completionRate >= 80 ? "#52c41a" : stats.completionRate >= 50 ? "#1890ff" : "#fa8c16"}
                />
              </Card>
            </Col>
          </Row>

          {/* Monthly Breakdown */}
          <Card size="small" className="mb-6" title="Monthly Breakdown">
            <div className="flex items-end gap-2" style={{ height: 120 }}>
              {monthlyData.map((item) => (
                <div key={item.month} className="flex-1 text-center">
                  {item.total > 0 ? (
                    <>
                      <div
                        className="mx-auto rounded-t"
                        style={{
                          width: "70%",
                          height: `${Math.max((item.percentage / 100) * 80, 4)}px`,
                          backgroundColor: item.percentage >= 80 ? "#1e3a5f" : item.percentage >= 50 ? "#3b82f6" : "#93c5fd",
                        }}
                      />
                      <div className="text-xs mt-1 font-medium">
                        {item.percentage}%
                      </div>
                    </>
                  ) : (
                    <div className="text-xs text-gray-300 mt-auto">—</div>
                  )}
                  <div className="text-xs text-gray-400 mt-1">{item.month}</div>
                </div>
              ))}
            </div>
          </Card>

          {/* Task List */}
          <Card size="small" title={`All Tasks (${tasks.length})`}>
            <div style={{ maxHeight: 400, overflow: "auto" }}>
              {tasks.map((task) => (
                <div
                  key={task.id}
                  className={"flex items-center justify-between border-b py-2 px-2" + (task.onHold ? " bg-yellow-50" : "")}
                >
                  <div className="flex items-center gap-2">
                    <div
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 2,
                        backgroundColor: task.completionStatus
                          ? "#52c41a"
                          : task.onHold
                          ? "#faad14"
                          : "#fa8c16",
                      }}
                    />
                    <span className={`text-sm ${task.completionStatus ? "line-through text-gray-400" : ""}`}>
                      {task.name}
                    </span>
                    {task.onHold && (
                      <Tag color="warning" className="text-xs">On Hold</Tag>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-400">
                    {task.taskEndDate && (
                      <span>Due: {moment(task.taskEndDate).format("MMM DD")}</span>
                    )}
                    <Tag
                      color={task.completionStatus ? "green" : task.onHold ? "gold" : "orange"}
                      className="text-xs"
                    >
                      {task.completionStatus ? "Done" : task.onHold ? "Hold" : "Pending"}
                    </Tag>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
